import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useConfirmDelete, useToast } from './ConfirmDialog';
import WA_STYLES from './MessagingStyles';

const API =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? window.location.origin
    : 'http://localhost:5000');

/* ─── helpers ────────────────────────────────────────────────── */
const useWinW = () => {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

const fmtTs = (d) => {
  if (!d) return '';
  const dt = new Date(d), now = new Date(), diff = now - dt;
  if (diff < 60000) return 'Ubu';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}min`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d`;
  return dt.toLocaleDateString('rw-RW');
};
const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString('rw-RW', { hour: '2-digit', minute: '2-digit' }) : '';
const fmtDateChip = (d) => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (diff === 0) return 'Uyu munsi';
  if (diff === 1) return 'Ejo hashize';
  return new Date(d).toLocaleDateString('rw-RW', { weekday: 'long', month: 'long', day: 'numeric' });
};
const isImg = (p) => /\.(jpe?g|png|gif|webp|svg)$/i.test(p || '');
const COLORS = ['#00a884','#7b4ea6','#e84393','#e65100','#1565c0','#2e7d32','#c62828','#00695c'];
const avColor = (n = '') => COLORS[(n.charCodeAt(0) || 0) % COLORS.length];

/* ─── Avatar ─────────────────────────────────────────────────── */
const Av = ({ name = '?', size = 42, style: st }) => (
  <div className="wa-av" style={{ width: size, height: size, fontSize: size * .38, background: avColor(name), ...st }}>
    {(name || '?').charAt(0).toUpperCase()}
  </div>
);

/* ─── SVG Icons (inline, no extra dep) ───────────────────────── */
const IcoSearch = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="#8696a0"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>;
const IcoSend = () => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>;
const IcoAttach = () => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M16.5 6v11.5a4 4 0 0 1-8 0V5a2.5 2.5 0 0 1 5 0v10.5a1 1 0 0 1-2 0V6H10v9.5a2.5 2.5 0 0 0 5 0V5a4 4 0 0 0-8 0v12.5a5.5 5.5 0 0 0 11 0V6h-1.5z"/></svg>;
const IcoEmoji = () => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16zm-3.5-8a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm7 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-3.5 5c2.33 0 4.31-1.46 5.11-3.5H6.89C7.69 15.54 9.67 17 12 17z"/></svg>;
const IcoMore = () => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>;
const IcoNewChat = () => <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/></svg>;
const IcoBroadcast = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M18 11v1a7 7 0 0 1-7 7H9.83l1.09 1.09L9.5 21.5 6 18l3.5-3.5 1.42 1.42L9.83 17H11a5 5 0 0 0 5-5v-1h2zm-5-9a7 7 0 0 1 7 7h-2a5 5 0 0 0-5-5V2zm0 4a3 3 0 0 1 3 3h-2a1 1 0 0 0-1-1V6zM3 11V3h8v2.18A7.02 7.02 0 0 0 5.07 11H3z"/></svg>;
const IcoBell = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 0 0 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>;
const IcoMsg = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/></svg>;
const IcoClose = () => <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>;
const IcoMute = () => <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0 0 14 7.97V10.18l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>;
const IcoArchive = () => <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zm-5.8-12l.81-1h9.99l.8 1H6.2z"/></svg>;
const IcoDelete = () => <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>;

/* ─── Right Panel ─────────────────────────────────────────────── */
const RightPanel = ({ conv, messages, onClose, onDelete }) => {
  const [lb, setLb] = useState(null);
  const media = useMemo(() =>
    messages.filter(m => m.file_path && isImg(m.file_path)).slice(-9).reverse(), [messages]);
  const src = (p) => p?.startsWith('http') ? p : `${API}${p}`;

  return (
    <div className="wa-panel">
      {lb && <div className="wa-lb" onClick={() => setLb(null)}><button className="wa-lb-close" onClick={() => setLb(null)}><IcoClose /></button><img src={lb} alt="" /></div>}
      <div className="wa-panel-head">
        <span className="wa-panel-title">Umwirondoro</span>
        <button className="wa-panel-close" onClick={onClose}><IcoClose /></button>
      </div>
      <div className="wa-panel-body">
        {conv ? (
          <>
            <div className="wa-panel-section" style={{ textAlign: 'center' }}>
              <div className="wa-panel-avatar" style={{ width: 80, height: 80, fontSize: 32, background: avColor(conv.other_user_name) }}>{(conv.other_user_name || '?').charAt(0).toUpperCase()}</div>
              <div style={{ fontWeight: 700, fontSize: 17, color: '#111b21', marginTop: 6 }}>{conv.other_user_name}</div>
              <div style={{ fontSize: 13, color: '#8696a0', marginTop: 3 }}>{conv.other_user_role}</div>
            </div>

            {media.length > 0 && (
              <div className="wa-panel-section">
                <div className="wa-panel-section-title">Amafoto ({media.length})</div>
                <div className="wa-media-grid">
                  {media.map(m => <img key={m.message_id} className="wa-media-thumb" src={src(m.file_path)} alt="" onClick={() => setLb(src(m.file_path))} onError={e => { e.target.style.display = 'none'; }} />)}
                </div>
              </div>
            )}

            <div className="wa-panel-action"><span className="wa-panel-action-icon"><IcoMute /></span> Heba amakuru</div>
            <div className="wa-panel-action"><span className="wa-panel-action-icon"><IcoArchive /></span> Bika</div>
            <div className="wa-panel-action danger" onClick={onDelete}><span className="wa-panel-action-icon"><IcoDelete /></span> Siba ikiganiro</div>
          </>
        ) : (
          <div style={{ padding: 24, color: '#8696a0', fontSize: 13, textAlign: 'center' }}>Hitamo ikiganiro</div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════════ */
const Messaging = () => {
  const token = localStorage.getItem('token') || '';
  const winW = useWinW();
  const isMobile = winW < 720;
  const showRightPanel = winW >= 1100;

  /* state */
  const [myInfo, setMyInfo] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [attached, setAttached] = useState(null);
  const [delId, setDelId] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('chats'); // chats | newchat | broadcasts | notifs
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [broadcasts, setBroadcasts] = useState([]);
  const [bcForm, setBcForm] = useState({ target_role: 'All', subject: '', message_text: '', priority: 'Normal' });
  const [showBcForm, setShowBcForm] = useState(false);
  const [selContact, setSelContact] = useState(null);
  const [firstMsg, setFirstMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [typing, setTyping] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [lb, setLb] = useState(null);
  const [showPanel, setShowPanel] = useState(false);

  const endRef = useRef(null);
  const txtRef = useRef(null);
  const fileRef = useRef(null);
  const sockRef = useRef(null);
  const typRef = useRef(null);
  const activeRef = useRef(null);
  useEffect(() => { activeRef.current = activeConv; }, [activeConv]);

  const confirmDelete = useConfirmDelete();
  const { toast } = useToast();

  const authH = useCallback(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);

  const apiFetch = useCallback(async (path, opts = {}) => {
    try {
      const res = await fetch(`${API}${path}`, { headers: authH(), ...opts });
      return res.ok ? res.json() : null;
    } catch { return null; }
  }, [authH]);

  const loadMe = useCallback(() => apiFetch('/messaging/me').then(d => d && setMyInfo(d)), [apiFetch]);
  const loadContacts = useCallback(() => apiFetch('/messaging/contacts').then(d => d && setContacts(d)), [apiFetch]);
  const loadConvs = useCallback(() => apiFetch('/messaging/conversations').then(d => d && setConversations(d)), [apiFetch]);
  const loadNotifs = useCallback(() => apiFetch('/messaging/notifications').then(d => d && setNotifs(d)), [apiFetch]);
  const loadBc = useCallback(() => apiFetch('/messaging/broadcasts').then(d => d && setBroadcasts(d)), [apiFetch]);
  const loadStats = useCallback(() => apiFetch('/messaging/stats').then(d => d && setStats(d)), [apiFetch]);
  const loadUnread = useCallback(() => apiFetch('/messaging/notifications/unread-count').then(d => d && setUnread(d.unread_count)), [apiFetch]);

  const loadMsgs = useCallback(async (cid) => {
    setLoading(true);
    const data = await apiFetch(`/messaging/conversations/${cid}/messages`);
    if (data) { setMessages(data); loadConvs(); loadUnread(); }
    setLoading(false);
  }, [apiFetch, loadConvs, loadUnread]);

  /* socket */
  useEffect(() => {
    if (!token) return;
    sockRef.current = io(API, { auth: { token }, transports: ['websocket', 'polling'] });
    sockRef.current.on('new_message', (data) => {
      const c = activeRef.current;
      if (c?.conversation_id === data.conversation_id)
        setMessages(p => p.some(m => m.message_id === data.message_id) ? p : [...p, { ...data, is_mine: 0 }]);
      loadConvs(); loadUnread();
    });
    sockRef.current.on('broadcast', () => { loadBc(); loadUnread(); });
    sockRef.current.on('user_typing', (d) => {
      if (activeRef.current?.conversation_id === d.conversation_id) {
        setTyping(true);
        setTimeout(() => setTyping(false), 3000);
      }
    });
    sockRef.current.on('user_stop_typing', () => setTyping(false));
    return () => sockRef.current?.disconnect();
  }, [token]); // eslint-disable-line

  useEffect(() => {
    if (!token) return;
    loadMe(); loadContacts(); loadConvs(); loadUnread(); loadStats(); loadBc();
  }, [token]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    const t = setInterval(() => { loadConvs(); loadUnread(); }, 15000);
    return () => clearInterval(t);
  }, [loadConvs, loadUnread]);

  /* send */
  const sendMsg = async (e) => {
    e?.preventDefault();
    const text = newMsg.trim();
    if ((!text && !attached) || !activeConv) return;
    const tmpId = `tmp-${Date.now()}`;
    const opt = { message_id: tmpId, message_text: text || null, is_mine: 1, created_at: new Date().toISOString() };
    setMessages(p => [...p, opt]);
    setNewMsg('');
    if (txtRef.current) txtRef.current.style.height = 'auto';
    try {
      let res;
      if (attached) {
        const fd = new FormData();
        if (text) fd.append('message_text', text);
        fd.append('file', attached);
        res = await fetch(`${API}/messaging/conversations/${activeConv.conversation_id}/messages`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      } else {
        res = await fetch(`${API}/messaging/conversations/${activeConv.conversation_id}/messages`, { method: 'POST', headers: authH(), body: JSON.stringify({ message_text: text }) });
      }
      if (res.ok) {
        const d = await res.json();
        setMessages(p => p.map(m => m.message_id === tmpId ? { ...opt, message_id: d.message_id, file_path: d.file_path || null, file_name: d.file_name || null } : m));
        setAttached(null);
        if (fileRef.current) fileRef.current.value = '';
        loadConvs();
      } else {
        setMessages(p => p.filter(m => m.message_id !== tmpId));
        setNewMsg(text);
      }
    } catch { setMessages(p => p.filter(m => m.message_id !== tmpId)); setNewMsg(text); }
  };

  const startConv = async (e) => {
    e.preventDefault();
    if (!selContact || !firstMsg.trim()) return;
    const res = await fetch(`${API}/messaging/conversations`, { method: 'POST', headers: authH(), body: JSON.stringify({ receiver_id: selContact.user_id, receiver_role: selContact.role, subject: `Chat`, message_text: firstMsg.trim() }) });
    if (!res.ok) return;
    const d = await res.json();
    setFirstMsg(''); setSelContact(null);
    const convs = await apiFetch('/messaging/conversations');
    if (convs) { setConversations(convs); const c = convs.find(x => x.conversation_id === d.conversation_id); if (c) openConv(c); }
  };

  const sendBc = async (e) => {
    e.preventDefault();
    if (!bcForm.message_text.trim()) return;
    const res = await fetch(`${API}/messaging/broadcast`, { method: 'POST', headers: authH(), body: JSON.stringify(bcForm) });
    if (res.ok) { setShowBcForm(false); setBcForm({ target_role: 'All', subject: '', message_text: '', priority: 'Normal' }); loadBc(); toast({ type: 'success', title: 'Itangazo ryoherejwe!' }); }
  };

  const openConv = (c) => {
    setActiveConv(c); setChatOpen(true);
    if (isMobile) setShowSidebar(false);
    loadMsgs(c.conversation_id);
  };

  const goBack = () => { setChatOpen(false); if (isMobile) setShowSidebar(true); };

  const delConv = async (cid) => {
    const ok = await confirmDelete({ title: 'Gusiba ikiganiro?', message: 'Ubutumwa bwose buzasibwa.', confirmText: '🗑️ Siba' });
    if (!ok) return;
    await fetch(`${API}/messaging/conversations/${cid}`, { method: 'DELETE', headers: authH() });
    loadConvs();
    toast({ type: 'success', title: 'Ikiganiro gasibwe' });
    if (activeConv?.conversation_id === cid) { setActiveConv(null); setChatOpen(false); if (isMobile) setShowSidebar(true); }
  };

  const delMsg = async (id) => {
    const ok = await confirmDelete({ title: 'Gusiba ubu butumwa?', confirmText: '🗑️ Siba' });
    if (!ok) return;
    setDelId(id);
    setTimeout(() => { setMessages(p => p.filter(m => m.message_id !== id)); setDelId(null); }, 220);
  };

  const emitTyping = () => {
    if (!activeConv || !sockRef.current) return;
    clearTimeout(typRef.current);
    sockRef.current.emit('typing', { conversation_id: activeConv.conversation_id, receiver_id: activeConv.other_user_id, receiver_role: activeConv.other_user_role });
    typRef.current = setTimeout(() => sockRef.current?.emit('stop_typing', { conversation_id: activeConv.conversation_id }), 2000);
  };

  const autoH = (el) => { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 120)}px`; };

  const markAllRead = async () => {
    await fetch(`${API}/messaging/notifications/mark-read`, { method: 'PUT', headers: authH(), body: '{}' });
    loadNotifs(); loadUnread();
  };

  /* filtered */
  const filtConvs = useMemo(() => conversations.filter(c => c.other_user_name?.toLowerCase().includes(search.toLowerCase())), [conversations, search]);
  const filtContacts = useMemo(() => contacts.filter(c => c.fullname?.toLowerCase().includes(search.toLowerCase()) || c.role?.toLowerCase().includes(search.toLowerCase())), [contacts, search]);

  /* date dividers */
  const msgsGrouped = useMemo(() => {
    const out = []; let last = '';
    messages.forEach(m => {
      const d = new Date(m.created_at).toDateString();
      if (d !== last) { out.push({ type: 'chip', label: fmtDateChip(m.created_at), key: `chip-${d}` }); last = d; }
      out.push({ type: 'msg', ...m });
    });
    return out;
  }, [messages]);

  const imgSrc = (p) => p?.startsWith('http') ? p : `${API}${p}`;

  if (!token) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', gap: 12, color: '#8696a0' }}>
      <div style={{ fontSize: 48 }}>🔒</div><div style={{ fontSize: 15, fontWeight: 600 }}>Banza winjire (Login)</div>
    </div>
  );

  /* ════ RENDER ════ */
  return (
    <>
      <style>{WA_STYLES}</style>

      {lb && <div className="wa-lb" onClick={() => setLb(null)}><button className="wa-lb-close" onClick={() => setLb(null)}><IcoClose /></button><img src={lb} alt="preview" /></div>}

      <div className="wa-root">

        {/* ══════════ SIDEBAR ══════════ */}
        <div className={`wa-sidebar${isMobile && !showSidebar ? ' wa-hidden' : ''}`}>

          {/* top bar */}
          <div className="wa-sb-topbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Av name={myInfo?.fullname || '?'} size={40} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#111b21', lineHeight: 1.2 }}>{myInfo?.fullname || '…'}</div>
                <div style={{ fontSize: 11.5, color: '#8696a0' }}>{myInfo?.role || ''}</div>
              </div>
            </div>
            <div className="wa-sb-topbar-actions">
              <button className="wa-topbar-btn" title="Ubutumwa Bushya" onClick={() => { setTab('newchat'); loadContacts(); }}><IcoNewChat /></button>
              <button className="wa-topbar-btn" title="Amatangazo" onClick={() => { setTab('broadcasts'); loadBc(); }}><IcoBroadcast /></button>
              <button className="wa-topbar-btn" title="Notifications" style={{ position: 'relative' }} onClick={() => { setTab('notifs'); loadNotifs(); }}>
                <IcoBell />
                {unread > 0 && <span className="wa-tab-badge" style={{ top: 2, right: 2 }}>{unread > 99 ? '99+' : unread}</span>}
              </button>
            </div>
          </div>

          {/* search */}
          <div className="wa-sb-search">
            <div className="wa-search-box">
              <span className="wa-search-icon"><IcoSearch /></span>
              <input
                placeholder="Shakisha cyangwa utangire ikiganiro"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', fontSize: 16, padding: 0 }}>×</button>}
            </div>
          </div>

          {/* tabs */}
          <div className="wa-sb-tabs">
            <button className={`wa-tab${tab === 'chats' ? ' active' : ''}`} onClick={() => setTab('chats')}>
              <span className="wa-tab-icon"><IcoMsg /></span>
              <span>Ubutumwa</span>
              {(stats.unread_messages > 0) && <span className="wa-tab-badge">{stats.unread_messages}</span>}
            </button>
            <button className={`wa-tab${tab === 'newchat' ? ' active' : ''}`} onClick={() => { setTab('newchat'); loadContacts(); }}>
              <span className="wa-tab-icon"><IcoNewChat /></span>
              <span>Bushya</span>
            </button>
            <button className={`wa-tab${tab === 'broadcasts' ? ' active' : ''}`} onClick={() => { setTab('broadcasts'); loadBc(); }}>
              <span className="wa-tab-icon"><IcoBroadcast /></span>
              <span>Tangazo</span>
              {(stats.unread_broadcasts > 0) && <span className="wa-tab-badge">{stats.unread_broadcasts}</span>}
            </button>
            <button className={`wa-tab${tab === 'notifs' ? ' active' : ''}`} onClick={() => { setTab('notifs'); loadNotifs(); }}>
              <span className="wa-tab-icon"><IcoBell /></span>
              <span>🔔</span>
              {unread > 0 && <span className="wa-tab-badge">{unread}</span>}
            </button>
          </div>

          {/* list */}
          <div className="wa-chatlist">

            {/* ── chats ── */}
            {tab === 'chats' && (
              filtConvs.length === 0 ? (
                <div className="wa-list-empty">
                  <div className="wa-list-empty-icon">💬</div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#111b21' }}>Nta biganiro</div>
                  <div style={{ fontSize: 13 }}>Kanda «Bushya» utangire ikiganiro</div>
                </div>
              ) : filtConvs.map(c => (
                <div key={c.conversation_id}
                  className={`wa-chat-item${activeConv?.conversation_id === c.conversation_id ? ' active' : ''}`}
                  onClick={() => openConv(c)}
                >
                  <div className="wa-chat-av-wrap">
                    <Av name={c.other_user_name} size={48} />
                    <div className="wa-online-ring" />
                  </div>
                  <div className="wa-chat-body">
                    <div className="wa-chat-header-row">
                      <span className="wa-chat-name">{c.other_user_name}</span>
                      <span className={`wa-chat-time${c.unread_count > 0 ? ' unread' : ''}`}>{fmtTs(c.last_message_time || c.updated_at)}</span>
                    </div>
                    <div className="wa-chat-preview-row">
                      <span className="wa-chat-preview">{c.last_message?.substring(0, 40) || 'Nta butumwa'}{c.last_message?.length > 40 ? '…' : ''}</span>
                      {c.unread_count > 0 && <span className="wa-unread-badge">{c.unread_count}</span>}
                      <button style={{ background: 'none', border: 'none', color: '#8696a0', cursor: 'pointer', fontSize: 14, marginLeft: 4, padding: '2px 4px', borderRadius: 4 }}
                        onClick={e => { e.stopPropagation(); delConv(c.conversation_id); }} title="Siba">✕</button>
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* ── new chat contacts ── */}
            {tab === 'newchat' && (
              <>
                <div className="wa-list-section">Hitamo umuntu ({filtContacts.length})</div>
                {filtContacts.map((c, i) => (
                  <div key={`${c.user_id}-${i}`}
                    className={`wa-contact-item${selContact?.user_id === c.user_id ? ' selected' : ''}`}
                    onClick={() => { setSelContact(c); if (isMobile) setShowSidebar(false); }}
                  >
                    <Av name={c.fullname} size={46} />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 15, color: '#111b21' }}>{c.fullname}</div>
                      <span className="wa-role-tag" style={{ background: c.role === 'Akagari' ? '#1565c0' : c.role === 'Mudugudu' ? '#2e7d32' : '#e65100' }}>{c.role}</span>
                    </div>
                  </div>
                ))}
                {filtContacts.length === 0 && <div className="wa-list-empty"><div className="wa-list-empty-icon">👥</div><div>Nta bantu babonetse</div></div>}
              </>
            )}

            {/* ── notifications ── */}
            {tab === 'notifs' && (
              <>
                <div className="wa-list-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 16px 5px' }}>
                  <span>Notifications</span>
                  {notifs.some(n => !n.is_read) && <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#00a884', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>✓ Soma byose</button>}
                </div>
                {notifs.length === 0
                  ? <div className="wa-list-empty"><div className="wa-list-empty-icon">🔔</div><div>Nta notifications</div></div>
                  : notifs.map(n => (
                    <div key={n.notification_id} className={`wa-notif-item${!n.is_read ? ' unread' : ''}`}
                      onClick={() => { const c = conversations.find(x => x.conversation_id === n.reference_id); if (c) openConv(c); }}>
                      <div style={{ fontWeight: n.is_read ? 400 : 600, fontSize: 14, color: '#111b21' }}>{n.title}</div>
                      <div style={{ fontSize: 12.5, color: '#8696a0', marginTop: 2 }}>{n.content?.substring(0, 60)}</div>
                      <div style={{ fontSize: 11, color: '#8696a0', marginTop: 3 }}>{fmtTs(n.created_at)}</div>
                    </div>
                  ))}
              </>
            )}

            {/* ── broadcasts ── */}
            {tab === 'broadcasts' && (
              <>
                {(myInfo?.role === 'Akagari' || myInfo?.role === 'Supadmin') && (
                  <div style={{ padding: '8px 12px' }}>
                    <button style={{ width: '100%', padding: '10px', border: 'none', borderRadius: 8, background: '#00a884', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                      onClick={() => { setShowBcForm(p => !p); if (isMobile) setShowSidebar(false); }}>
                      📢 {showBcForm ? 'Bireke' : 'Ohereza Itangazo'}
                    </button>
                  </div>
                )}
                {broadcasts.length === 0 && !showBcForm
                  ? <div className="wa-list-empty"><div className="wa-list-empty-icon">📢</div><div>Nta matangazo</div></div>
                  : broadcasts.map(b => (
                    <div key={b.broadcast_id} className={`wa-bc-item${!b.is_read ? ' unread' : ''}`}
                      style={{ borderLeftColor: b.priority === 'Urgent' ? '#e53935' : b.priority === 'High' ? '#f59f00' : '#8696a0' }}
                      onClick={async () => { if (!b.is_read) { await fetch(`${API}/messaging/broadcasts/${b.broadcast_id}/read`, { method: 'PUT', headers: authH() }); loadBc(); loadStats(); } }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontWeight: 600, fontSize: 14, color: '#111b21' }}>{b.subject || 'Itangazo'}</span>
                        <span style={{ background: b.priority === 'Urgent' ? '#e53935' : b.priority === 'High' ? '#f59f00' : '#8696a0', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{b.priority}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#8696a0', marginBottom: 3 }}>{b.message_text?.substring(0, 70)}{b.message_text?.length > 70 ? '…' : ''}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8696a0' }}>
                        <span>{b.sender_name}</span><span>{fmtTs(b.created_at)}</span>
                      </div>
                    </div>
                  ))}
              </>
            )}

          </div>
        </div>

        {/* ══════════ MAIN AREA ══════════ */}
        <div className="wa-main">

          {/* ── active chat ── */}
          {chatOpen && activeConv ? (
            <>
              {/* chat header */}
              <div className="wa-chat-head">
                {isMobile && <button className="wa-back-btn" onClick={goBack}>‹</button>}
                <div className="wa-chat-av-wrap" style={{ cursor: 'pointer' }} onClick={() => setShowPanel(p => !p)}>
                  <Av name={activeConv.other_user_name} size={40} />
                  <div className="wa-online-ring" />
                </div>
                <div className="wa-chat-head-info" onClick={() => setShowPanel(p => !p)}>
                  <div className="wa-chat-head-name">{activeConv.other_user_name}</div>
                  <div className="wa-chat-head-sub">
                    <div className="wa-online-dot" />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{typing ? 'Arimo yandika…' : (activeConv.other_user_role || 'Online')}</span>
                  </div>
                </div>
                <div className="wa-head-actions">
                  <button className="wa-head-btn wa-head-extra" title="Shakisha">🔍</button>
                  <button className="wa-head-btn wa-head-extra" title="Guhamagara">📞</button>
                  <button className="wa-head-btn wa-head-extra" title="Vidiyo">📹</button>
                  <button className="wa-head-btn" title="Ibindi" onClick={() => setShowPanel(p => !p)}><IcoMore /></button>
                </div>
              </div>

              {/* messages */}
              <div className="wa-messages">
                {loading
                  ? <div style={{ textAlign: 'center', color: '#8696a0', padding: 40 }}>⏳ Gutegereza…</div>
                  : messages.length === 0
                  ? <div style={{ textAlign: 'center', color: '#8696a0', padding: 40 }}><div style={{ fontSize: 48, marginBottom: 8 }}>👋</div><div style={{ fontSize: 14 }}>Tangira ubutumwa!</div></div>
                  : msgsGrouped.map(item => {
                    if (item.type === 'chip') return (
                      <div key={item.key} className="wa-date-chip"><span>{item.label}</span></div>
                    );
                    const m = item, mine = !!m.is_mine;
                    return (
                      <div key={m.message_id} className={`wa-msg-row ${mine ? 'sent' : 'recv'}`}
                        style={{ opacity: delId === m.message_id ? 0 : 1, transition: 'opacity .2s' }}>
                        {!mine && <div className="wa-sender-label" style={{ color: avColor(m.sender_name) }}>{m.sender_name}</div>}
                        <div className="wa-bubble-wrap">
                          {mine && <button className="wa-del-msg" onClick={() => delMsg(m.message_id)} title="Siba"><IcoDelete /></button>}
                          <div className={`wa-bubble ${mine ? 'sent' : 'recv'}`}>
                            {m.message_text && <div className="wa-bubble-text">{m.message_text}</div>}
                            {m.file_path && isImg(m.file_path) && (
                              <img className="wa-bubble-img" src={imgSrc(m.file_path)} alt=""
                                onClick={() => setLb(imgSrc(m.file_path))}
                                onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                            )}
                            {m.file_path && !isImg(m.file_path) && (
                              <a className="wa-file-bubble" href={imgSrc(m.file_path)} target="_blank" rel="noreferrer">
                                <span className="wa-file-icon">📄</span>
                                <div className="wa-file-info">
                                  <div className="wa-file-name">{m.file_name || 'Dosiye'}</div>
                                  <div className="wa-file-dl">Kuramo ↓</div>
                                </div>
                              </a>
                            )}
                            <div className="wa-bubble-foot">
                              <span className="wa-bubble-time">{fmtTime(m.created_at)}</span>
                              {mine && <span className={`wa-ticks ${m.read_at ? 'read' : 'delivered'}`}>✓✓</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                }
                {typing && (
                  <div className="wa-typing-wrap">
                    <div className="wa-typing">
                      <div className="wa-tdot" /><div className="wa-tdot" /><div className="wa-tdot" />
                    </div>
                  </div>
                )}
                <div ref={endRef} />
              </div>

              {/* attach preview */}
              {attached && (
                <div className="wa-attach-strip">
                  <span>{attached.type.startsWith('image/') ? '🖼️' : '📄'}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attached.name}</span>
                  <button className="wa-attach-strip-x" type="button" onClick={() => { setAttached(null); if (fileRef.current) fileRef.current.value = ''; }}>✕</button>
                </div>
              )}

              {/* input bar */}
              <form className="wa-input-bar" onSubmit={sendMsg}>
                <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }} onChange={e => setAttached(e.target.files[0] || null)} />
                <button type="button" className="wa-input-icon-btn" title="Emoji"><IcoEmoji /></button>
                <button type="button" className="wa-input-icon-btn" title="Shyiraho dosiye" onClick={() => fileRef.current?.click()}><IcoAttach /></button>
                <textarea
                  ref={txtRef}
                  className="wa-input-box"
                  rows={1}
                  placeholder="Andika ubutumwa..."
                  value={newMsg}
                  onChange={e => { setNewMsg(e.target.value); autoH(e.target); emitTyping(); }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
                  autoFocus={!isMobile}
                />
                <button type="submit" className="wa-send-btn" disabled={!newMsg.trim() && !attached}><IcoSend /></button>
              </form>
            </>

          ) : tab === 'newchat' ? (
            /* ── new chat form ── */
            <div className="wa-form-wrap">
              <div className="wa-form-card">
                {isMobile && <button className="wa-back-link" onClick={() => { setShowSidebar(true); setSelContact(null); }}>‹ Subira inyuma</button>}
                <div className="wa-form-title"><IcoNewChat /> Ubutumwa Bushya</div>
                {selContact ? (
                  <form onSubmit={startConv}>
                    <div className="wa-selected-contact-card">
                      <Av name={selContact.fullname} size={44} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: '#111b21' }}>{selContact.fullname}</div>
                        <span className="wa-role-tag" style={{ background: selContact.role === 'Akagari' ? '#1565c0' : '#2e7d32' }}>{selContact.role}</span>
                      </div>
                      <button type="button" onClick={() => { setSelContact(null); if (isMobile) setShowSidebar(true); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#8696a0' }}>✕</button>
                    </div>
                    <label className="wa-form-lbl">Ubutumwa</label>
                    <textarea className="wa-form-ctrl" style={{ minHeight: 90, resize: 'vertical' }} value={firstMsg} onChange={e => setFirstMsg(e.target.value)} placeholder="Andika ubutumwa bwawe..." rows={4} autoFocus={!isMobile} />
                    <button type="submit" className="wa-form-btn" disabled={!firstMsg.trim()}>Ohereza ›</button>
                  </form>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: '#8696a0' }}>
                    <div style={{ fontSize: 52, marginBottom: 10 }}>{isMobile ? '👆' : '👈'}</div>
                    <div style={{ fontSize: 14 }}>{isMobile ? 'Subira inyuma uhitemo umuntu' : 'Hitamo umuntu kurubuga rw\'ibumoso'}</div>
                  </div>
                )}
              </div>
            </div>

          ) : tab === 'broadcasts' && showBcForm ? (
            /* ── broadcast form ── */
            <div className="wa-form-wrap">
              <div className="wa-form-card">
                {isMobile && <button className="wa-back-link" onClick={() => { setShowBcForm(false); setShowSidebar(true); }}>‹ Subira inyuma</button>}
                <div className="wa-form-title">📢 Ohereza Itangazo</div>
                <form onSubmit={sendBc}>
                  <label className="wa-form-lbl">Kohereza</label>
                  <select className="wa-form-ctrl" value={bcForm.target_role} onChange={e => setBcForm(p => ({ ...p, target_role: e.target.value }))}>
                    <option value="All">Bose</option><option value="Mudugudu">Imidugudu gusa</option><option value="Akagari">Utuagari gusa</option>
                  </select>
                  <label className="wa-form-lbl">Umutwe</label>
                  <input className="wa-form-ctrl" value={bcForm.subject} onChange={e => setBcForm(p => ({ ...p, subject: e.target.value }))} placeholder="Umutwe w'itangazo..." />
                  <label className="wa-form-lbl">Ubutumwa</label>
                  <textarea className="wa-form-ctrl" style={{ minHeight: 100, resize: 'vertical' }} value={bcForm.message_text} onChange={e => setBcForm(p => ({ ...p, message_text: e.target.value }))} placeholder="Andika itangazo..." rows={5} />
                  <label className="wa-form-lbl">Priority</label>
                  <select className="wa-form-ctrl" value={bcForm.priority} onChange={e => setBcForm(p => ({ ...p, priority: e.target.value }))}>
                    <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
                  </select>
                  <button type="submit" className="wa-bc-send-btn" disabled={!bcForm.message_text.trim()}>📢 Ohereza</button>
                </form>
              </div>
            </div>

          ) : (
            /* ── welcome ── */
            <div className="wa-welcome">
              <div style={{ fontSize: 80, filter: 'grayscale(.2)' }}>💬</div>
              <div style={{ fontSize: 28, fontWeight: 300, color: '#41525d', letterSpacing: -.5 }}>Igitabo Messaging</div>
              <div style={{ fontSize: 14, color: '#8696a0', maxWidth: 360, lineHeight: 1.7 }}>
                Ohereza no kwakira ubutumwa byihuse. Hitamo ikiganiro kurubuga rw'ibumoso utangire.
              </div>
              <div className="wa-welcome-lock">
                🔒 <span>Ubutumwa bufite umutekano</span>
              </div>
              <div className="wa-stats-row">
                {[{ v: stats.total_conversations || 0, l: 'Ibiganiro' }, { v: stats.unread_messages || 0, l: 'Bishya' }, { v: stats.unread_broadcasts || 0, l: 'Tangazo' }].map(s => (
                  <div key={s.l} className="wa-stat-box"><div className="wa-stat-num">{s.v}</div><div className="wa-stat-lbl">{s.l}</div></div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ══════════ RIGHT PANEL ══════════ */}
        {(showRightPanel || showPanel) && (
          <RightPanel
            conv={chatOpen ? activeConv : null}
            messages={messages}
            onClose={() => setShowPanel(false)}
            onDelete={() => activeConv && delConv(activeConv.conversation_id)}
          />
        )}

      </div>
    </>
  );
};

export default Messaging;
